import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import DataContext from '../../../Contexts/DataContext';

export const ACCT_TYPE_OPTIONS = [
  'Revenue',
  'Expense',
  'Deduction',
  'Other Revenue',
  'Asset',
  'Liability',
  'Equity',
  'Stat',
  'P&L',
];

export const FIXED_VARIABLE_OPTIONS = ['Fixed', 'Variable', 'Stat'];

const INITIAL_FORM = {
  account_id: '',
  account_name: '',
  parent_account: '',
  acct_type: '',
  fixed_or_variable: '',
};

/**
 * AddSeedAccountDialog — controlled MUI Dialog for creating a new seed account
 * inline within the Pro Forma wizard.
 *
 * Props:
 *   open             {boolean}           — controls dialog visibility
 *   entityId         {string}            — pre-filled entity ID (read-only context)
 *   entityName       {string}            — pre-filled entity name (read-only context)
 *   departmentId     {string}            — pre-filled department ID (read-only context)
 *   departmentName   {string}            — pre-filled department name (read-only context)
 *   parentAccounts   {Array}             — [{ id, parent_account_name }]
 *   onClose          {() => void}        — called on cancel or backdrop click; no context call
 *   onCreated        {(account) => void} — called with the created account object on success
 */
const AddSeedAccountDialog = ({
  open,
  entityId,
  entityName,
  departmentId,
  departmentName,
  parentAccounts,
  onClose,
  onCreated,
}) => {
  const context = useContext(DataContext);

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Reset form state whenever the dialog opens
  useEffect(() => {
    if (open) {
      setForm(INITIAL_FORM);
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    const newErrors = {};
    const trimmedId = String(form.account_id).trim();
    const trimmedName = String(form.account_name).trim();

    if (!trimmedId) {
      newErrors.account_id = 'Account ID is required';
    } else if (!/^\d+$/.test(trimmedId)) {
      newErrors.account_id = 'Account ID must be numeric';
    } else {
      // Client-side duplicate check: GL key must be unique within this entity+department
      const existingAccounts = context.activeProjectAttributes?.accountsMetadata || [];
      const glKey = `${entityId}-${departmentId}-${trimmedId}`;
      const isDuplicate = existingAccounts.some(
        (a) => String(a.gl_key).trim() === glKey
      );
      if (isDuplicate) {
        newErrors.account_id = `GL Key ${glKey} already exists. Choose a different Account ID.`;
      }
    }

    if (!trimmedName) {
      newErrors.account_name = 'Account Name is required';
    }

    if (!form.parent_account && form.parent_account !== 0) {
      newErrors.parent_account = 'Parent Account is required';
    }

    if (!form.acct_type) {
      newErrors.acct_type = 'Account Type is required';
    }

    if (!form.fixed_or_variable) {
      newErrors.fixed_or_variable = 'Fixed or Variable is required';
    }

    return newErrors;
  };

  const buildDates = () => {
    const projectStart = context.activeProjectInfo?.start_date;
    const projectEnd = context.activeProjectInfo?.end_date;
    const dates = {};
    if (projectStart && projectEnd) {
      const start = new Date(`${projectStart}T00:00:00`);
      // Backend stores project dates as the last day of the previous month (e.g., Jan 31 = Feb start).
      // Adding 1 day converts to the first of the represented month.
      start.setDate(start.getDate() + 1);
      const end = new Date(`${projectEnd}T00:00:00`);
      end.setDate(end.getDate() + 1);
      const cur = new Date(start.getFullYear(), start.getMonth(), 1);
      while (cur <= end) {
        const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-01`;
        dates[key] = 0;
        cur.setMonth(cur.getMonth() + 1);
      }
    }
    return dates;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      const dates = buildDates();
      const payload = {
        entity_id: entityId,
        entity: entityName,
        department_id: departmentId,
        department_name: departmentName,
        account_id: String(form.account_id).trim(),
        account_name: String(form.account_name).trim(),
        parent_account: form.parent_account,
        acct_type: form.acct_type,
        fixed_or_variable: form.fixed_or_variable,
        dates,
      };

      const result = await context.createAccount(payload);
      if (result) {
        onCreated(result);
        handleClose();
      } else {
        // Backend rejected — most likely a duplicate GL key that slipped past the client check
        setErrors({ account_id: `GL Key ${entityId}-${departmentId}-${payload.account_id} already exists. Choose a different Account ID.` });
      }
    } catch (err) {
      console.error('Error creating account:', err);
      toast.error('Failed to create account. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    onClose();
  };

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear the error for this field as the user types/selects
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Seed Account</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Read-only entity/department context */}
          <Typography variant="body2" color="text.secondary">
            Entity: {entityId} – {entityName} | Department: {departmentId} – {departmentName}
          </Typography>

          {/* Account ID */}
          <TextField
            label="Account ID *"
            size="small"
            fullWidth
            value={form.account_id}
            onChange={handleFieldChange('account_id')}
            error={!!errors.account_id}
            helperText={errors.account_id || 'Must be numeric (e.g. 4000)'}
            disabled={saving}
            inputProps={{ inputMode: 'numeric' }}
          />

          {/* Account Name */}
          <TextField
            label="Account Name *"
            size="small"
            fullWidth
            value={form.account_name}
            onChange={handleFieldChange('account_name')}
            error={!!errors.account_name}
            helperText={errors.account_name}
            disabled={saving}
          />

          {/* Parent Account */}
          <FormControl size="small" fullWidth error={!!errors.parent_account} disabled={saving}>
            <InputLabel id="parent-account-label">Parent Account *</InputLabel>
            <Select
              labelId="parent-account-label"
              label="Parent Account *"
              value={form.parent_account}
              onChange={handleFieldChange('parent_account')}
            >
              {(parentAccounts || []).map((pa) => (
                <MenuItem key={pa.id ?? pa.parent_account_name} value={pa.parent_account_name}>
                  {pa.parent_account_name}
                </MenuItem>
              ))}
            </Select>
            {errors.parent_account && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.parent_account}
              </Typography>
            )}
          </FormControl>

          {/* Account Type */}
          <FormControl size="small" fullWidth error={!!errors.acct_type} disabled={saving}>
            <InputLabel id="acct-type-label">Account Type *</InputLabel>
            <Select
              labelId="acct-type-label"
              label="Account Type *"
              value={form.acct_type}
              onChange={handleFieldChange('acct_type')}
            >
              {ACCT_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
            {errors.acct_type && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.acct_type}
              </Typography>
            )}
          </FormControl>

          {/* Fixed or Variable */}
          <FormControl size="small" fullWidth error={!!errors.fixed_or_variable} disabled={saving}>
            <InputLabel id="fixed-variable-label">Fixed or Variable *</InputLabel>
            <Select
              labelId="fixed-variable-label"
              label="Fixed or Variable *"
              value={form.fixed_or_variable}
              onChange={handleFieldChange('fixed_or_variable')}
            >
              {FIXED_VARIABLE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
            {errors.fixed_or_variable && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.fixed_or_variable}
              </Typography>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Creating...' : 'Add Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSeedAccountDialog;
